/**
 * Non-component helpers for the voice window's morphs: measuring a face,
 * pinning the box's width, and keeping a leaving element mounted for the one
 * beat its exit animation needs.
 *
 * Every duration lives in voice-stage.module.css; the two numbers here are the
 * ones JavaScript cannot read from the sheet — a mount lease and a safety net.
 */
import { useEffect, useRef, useState } from "react"

/**
 * One Guiding beat (`--dur-guiding`), long enough for `guide-out` to play on
 * an element the parent has already dropped. Nothing else is timed with it.
 */
export const VOICE_GUIDING_MS = 220

/**
 * A ghost that never gets its `animationend` — a backgrounded tab, a dropped
 * frame — is swept by this timer. Longer than Focus, so it never cuts a real
 * exit short.
 */
export const MORPH_EXIT_FALLBACK_MS = 900

/**
 * Measure the face where it will settle and pin the box to it.
 *
 * The box is let go for one style pass — `data-still` both stops the
 * transition and frees the face to lay out as it does at rest, wrapping and
 * all — so `offsetWidth` is the width the morph ends at, not the one the box
 * happens to be passing through. That number goes on the box as `--morph-w`,
 * which the face then holds for the whole morph: the box's sides clip it
 * instead of the text re-wrapping while the width eases.
 *
 * A morph eases from where the box is drawn right now, so a change landing
 * mid-tween retargets instead of jumping. A first paint and every remeasure
 * (font load, locale, a smaller stage) take the new width at once. Nothing is
 * painted between the lines below.
 */
export function setMorphWidth(box: HTMLElement, face: HTMLElement, animate: boolean): void {
  const previous = box.style.width
  const drawn = box.getBoundingClientRect().width

  box.setAttribute("data-still", "true")
  box.style.width = ""
  const target = face.offsetWidth

  // Nothing is laid out (jsdom, a window that is not shown yet): the content
  // keeps the width, and the next remeasure settles it.
  if (target <= 0) {
    box.style.width = previous
    box.removeAttribute("data-still")
    return
  }

  box.style.setProperty("--morph-w", `${target}px`)
  box.style.width = animate ? `${drawn}px` : `${target}px`
  // Reading layout applies the two lines above while the transition is off.
  box.getBoundingClientRect()
  box.removeAttribute("data-still")
  if (animate) box.style.width = `${target}px`
}

/** `true` while the box is still easing to the width of its last morph. */
export function isMorphEasing(box: HTMLElement): boolean {
  return typeof box.getAnimations === "function" && box.getAnimations().length > 0
}

/**
 * `true` while `show` is true and for `ms` after it turns false, so whatever
 * the parent has dropped stays mounted long enough to leave.
 */
export function useLingering(show: boolean, ms: number): boolean {
  const [lingering, setLingering] = useState(show)

  useEffect(() => {
    if (show) {
      setLingering(true)
      return
    }
    const timer = window.setTimeout(() => setLingering(false), ms)
    return () => window.clearTimeout(timer)
  }, [ms, show])

  return show || lingering
}

/** `true` once `value` has differed from the one this component first saw. */
export function useHasChanged(value: string): boolean {
  const firstRef = useRef(value)
  const [changed, setChanged] = useState(false)

  useEffect(() => {
    if (changed || value === firstRef.current) return
    setChanged(true)
  }, [changed, value])

  return changed
}
